import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AxisDomain } from "recharts/types/util/types";

import {
  BaselineListOptionsBar,
  IPatientBaseline,
  IPatientBaselineData,
} from "../../../../../../@types/Baselines";
import {
  MeasurementTypes,
  SelectMeasurementPayloadType,
} from "../../../../../../@types/Measurements";
import { Notification } from "../../../../../../@types/Notification";
import {
  IDataDisplayPreferences,
  IUserPreferences,
} from "../../../../../../@types/Preferences";
import { IPatientTreatment } from "../../../../../../@types/Treatments";
import { ChartConfig } from "../../../../../../components/ChartCard/ChartCard.types";
import { ModalChart } from "../../../../../../components/ModalChart";
import { setSelectedPoint } from "../../../../../../components/ModalChart/selectedPointStore";
import { AUTH_UNIT_PREFERENCES } from "../../../../../../constants/localStorageKeys";
import {
  defaultDataDisplayConfig,
  spirometryMeasurementsValues,
  spirometryQualityFilterTypes,
} from "../../../../../../constants/measurements";
import { useMultipleMeasurements } from "../../../../../../hooks/queries/measurements";
import {
  usePatientInfo,
  usePatientWards,
} from "../../../../../../hooks/queries/patients";
import { useTreatments } from "../../../../../../hooks/queries/treatments";
import useIsMobile from "../../../../../../hooks/useIsMobile";
import { useToast } from "../../../../../../hooks/useToast";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { userService } from "../../../../../../services/userService";
import { getSourceLegends } from "../../../../../../utils/chartSourceUtils";
import {
  getTrendLineDisplaySettingFromLocalStorage,
  NebulizerChartRow,
} from "../../../../../../utils/chartUtils";
import { getShortFormattedDate } from "../../../../../../utils/dateFormatter";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../../../../../utils/storage";
import {
  isWardFeatureEnabledForPatient,
  resolvePatientWardIds,
} from "../../../../../../utils/wardConfig";
import {
  MeasurementContainer,
  HeaderContainer,
  Title,
  Subtitle,
} from "../../MeasurementModal.styles";
import { MeasurementModalProps } from "../../MeasurementModal.types";
import {
  CustomOptionsBar,
  DATE_FILTER_48H,
  DATE_FILTER_ALL,
  DATE_FILTER_CUSTOM,
} from "../CustomOptionsBar";
import { ModalDialysisTable } from "../ModalDialysisTable/ModalDialysisTable";
import { ModalMeasurementsTable } from "../ModalMeasurementsTable/ModalMeasurementsTable";
import { ModalNebulizerTable } from "../ModalNebulizerTable/ModalNebulizerTable";
import { ModalSkeleton } from "../ModalSkeleton";
import { NebulizerModalChart } from "../NebulizerModalChart";
import { ModalOptions } from "../OptionsPopover/Options.types";
import { SubtitleConatiner } from "./MeasurementSection.styles";

type MeasurementSectionProps = Omit<
  MeasurementModalProps,
  "spirometryMeasurements"
> & {
  onSelectChartValue?: (selectedChartValue: any) => void;
  onSelectedTableView: (showMeasurementsTable: boolean) => void;
  isPinnedGraph?: boolean;
  checkNewData?: (mostRecentDate: dayjs.Dayjs) => void;
  onTreatmentClick?: (
    treatmnetData: NebulizerChartRow | IPatientTreatment
  ) => void;
  closeTreatmentDetails?: () => void;
};

export function MeasurementSection({
  patientId,
  patient,
  cardTitle,
  measurementType,
  minValue,
  maxValue,
  onSelectChartValue,
  onSelectedTableView,
  isPinnedGraph,
  unit,
  checkNewData,
  onTreatmentClick,
  closeTreatmentDetails,
}: MeasurementSectionProps) {
  const { t } = useTranslation();

  const { showToast } = useToast();

  const isSpirometry =
    spirometryMeasurementsValues.indexOf(measurementType) !== -1;

  const {
    preferences,
    changeUserPreferences,
    portalPreferences,
    changePortalPreferences,
    changeUnitPreferences,
    getDateRangeFilter,
  } = useUserPreferences();

  // Use React Query directly to get fresh ishltBaselineActive value
  // This ensures the component re-renders when the query is invalidated after toggle
  const { patient: patientFromQuery } = usePatientInfo({ patientId });
  const ishltBaselineActive =
    patientFromQuery?.ishltBaselineActive ??
    patient?.ishltBaselineActive ??
    false;

  // EN-4960: ISHLT baselines are enabled when ANY of the patient's wards enables the flag
  // (unioned across the full ward set, falling back to the primary ward), not just the primary.
  const { patientWards } = usePatientWards(patient?.id);
  const wardISHLTBaselineActive = useMemo(
    () =>
      isWardFeatureEnabledForPatient(
        resolvePatientWardIds(patientWards?.wardIds, patient?.hospitalId),
        "enableISHLTBaselines"
      ),
    [patientWards, patient?.hospitalId]
  );

  const [spirometryTestIdValue, setSpirometryTestIdValue] = useState<
    string | null | undefined
  >();

  const shouldAutoSelectLastTest = useRef(true);

  const getGraphPreferences = (): string => {
    const userUnitPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);
    if (!userUnitPreferences) return "";

    let dataPreferences: IDataDisplayPreferences;

    const parsedUnitPreferences = safeJsonParse<IUserPreferences | null>(
      userUnitPreferences,
      null
    );

    if (!parsedUnitPreferences) return "";

    try {
      const rawDataPreferences = parsedUnitPreferences.dataDisplayPreference;

      dataPreferences =
        typeof rawDataPreferences === "string"
          ? JSON.parse(rawDataPreferences)
          : rawDataPreferences;

      if (typeof dataPreferences !== "object" || dataPreferences === null) {
        throw new Error("Invalid dataDisplayPreference format");
      }
    } catch {
      dataPreferences = defaultDataDisplayConfig;

      const newUnitPreferences: IUserPreferences = {
        ...parsedUnitPreferences,
        dataDisplayPreference: JSON.stringify(defaultDataDisplayConfig),
      };

      changeUnitPreferences(newUnitPreferences);
    }

    const updatedFilter =
      dataPreferences[measurementType as keyof IDataDisplayPreferences];
    return updatedFilter || dataPreferences.default || "";
  };

  const [showTrendLine, setShowTrendLine] = useState<boolean>(
    getTrendLineDisplaySettingFromLocalStorage(measurementType)
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(getGraphPreferences);

  const { widthView } = useIsMobile();

  let dateRangeFilter = getDateRangeFilter();

  const date = searchParams.get("date");
  if (dayjs(date).isValid()) {
    dateRangeFilter = {
      startDate: dayjs(date),
      endDate: dayjs(date),
      id: "",
    };
  }

  const [showMeasurementsTable, setShowMeasurementsTable] =
    useState<boolean>(false);

  const [formattedDateRange, setFormattedDateRange] = useState<string>("");
  /**
   * The chart's earliest real point, once the data has arrived. The subtitle has always
   * resolved this for "ALL"; keeping it lets the date picker open on the same date instead of
   * the 1900-01-01 sentinel that "ALL" carries as its start.
   */
  const [dataStartDate, setDataStartDate] = useState<dayjs.Dayjs | null>(null);

  const { hideFvcRejected, hideFev1Rejected, hideFvcUsable, hideFev1Usable } =
    preferences;

  const chartConfigs: ChartConfig[] = [];
  const allMeasurements: Map<string, any[]> = new Map<string, any[]>();
  const allPatientBaselines: Map<string, IPatientBaselineData> = new Map<
    string,
    IPatientBaselineData
  >();

  let mainValues: any = {
    min: 0,
    max: 0,
    average: 0,
  };

  let diastolicMainValues: any = {
    min: 0,
    max: 0,
    average: 0,
  };

  const measurementTypes: MeasurementTypes[] = [measurementType];

  if (measurementType === "bp_systolic") {
    measurementTypes.push("bp_diastolic");
  }

  const { data, refetch, isRefetching, isFetching } = useMultipleMeasurements({
    patientId,
    zoomedVersion: true,
    types: measurementTypes.filter(
      (item) =>
        item !== "nebulizer" &&
        item !== "fitbit_activity" &&
        item !== "fitbit_sleep"
    ),
    toDate: dateRangeFilter.endDate.format(),
    fromDate: dateRangeFilter.startDate.format(),
    filter,
    hideFvcRejected,
    hideFev1Rejected,
    hideFvcUsable,
    hideFev1Usable,
  });

  const {
    treatments,
    treatmentsRefetch,
    isTreatmentsFetching,
    isTreatmentsRefetching,
  } = useTreatments(
    parseInt(patientId ?? "0", 10),
    dateRangeFilter.id === DATE_FILTER_48H ||
      dateRangeFilter.id === DATE_FILTER_ALL
      ? dateRangeFilter.startDate
      : dateRangeFilter.startDate.startOf("day"),
    dateRangeFilter.endDate.endOf("day"),
    measurementType
  );

  const getNebulizerMinAndMaxDates = () => {
    return treatments.reduce(
      (acc, t) => {
        const ts = new Date(t.time).getTime();
        if (!Number.isFinite(ts)) return acc;

        if (acc.min === null || ts < acc.min) acc.min = ts;
        if (acc.max === null || ts > acc.max) acc.max = ts;

        return acc;
      },
      { min: null as number | null, max: null as number | null }
    );
  };

  if (data && data.length > 0 && data[0]) {
    const { mainMeasurementValues } = data[0] as any;

    mainValues = mainMeasurementValues;
    if (measurementType === "bp_systolic") {
      if (data[1]) {
        const { mainMeasurementValues } = data[1] as any;
        diastolicMainValues = mainMeasurementValues;
      }
    }

    if (
      shouldAutoSelectLastTest.current &&
      spirometryTestIdValue !== mainValues.lastSpirometryTestId &&
      isSpirometry
    ) {
      setSpirometryTestIdValue(mainValues.lastSpirometryTestId);
    }

    data.forEach((d: any) => {
      if (!d) return;

      const { type, measurements, patientBaselines } = d;

      const activeBaseline = (patientBaselines as IPatientBaseline[])?.find(
        (x: IPatientBaseline) =>
          x.archived === false && x.isISHLTBaseline === false
      )?.baseline;

      const activeISHLTBaseline = (
        patientBaselines as IPatientBaseline[]
      )?.find(
        (x: IPatientBaseline) =>
          x.archived === false && x.isISHLTBaseline === true
      )?.baseline;

      allPatientBaselines.set(type, {
        activeBaseline,
        activeISHLTBaseline,
        baselines: ishltBaselineActive
          ? patientBaselines
          : (patientBaselines as IPatientBaseline[])?.filter(
              (pb: IPatientBaseline) => !pb.isISHLTBaseline
            ),
      });

      if (d.mainMeasurementValues.maxDate && checkNewData) {
        checkNewData(d.mainMeasurementValues.maxDate);
      }

      if (measurements && measurements.size > 0) {
        measurements.forEach((measurementsMap: any, key: string) => {
          const source = (key || "unknown").toLowerCase();

          const sourceLegend = getSourceLegends(type, source);
          const { color, shape, lineOpacity, isDotClickable } = sourceLegend;

          if (measurementType === "bp_systolic" && source === "apple-health") {
            return;
          }

          allMeasurements.set(`${type}|${source}`, measurementsMap);
          chartConfigs.push({
            dot: false,
            dotShape: shape,
            lineType: "monotone",
            yKey: type,
            color,
            lineOpacity,
            isDotClickable,
            selectedSpirometryTestId: spirometryTestIdValue
              ? parseInt(spirometryTestIdValue, 10)
              : undefined,
            min: minValue,
            max: maxValue,
            source,
          });
        });
      }
    });
  }

  useEffect(() => {
    if (showMeasurementsTable) setSpirometryTestIdValue(null);
  }, [showMeasurementsTable]);

  useEffect(() => {
    const spirometryTestId = searchParams.get("spirometryTestId");
    setSpirometryTestIdValue(spirometryTestId);

    return () => {
      const spirometryTestId = searchParams.get("spirometryTestId");

      if (spirometryTestId == null && isPinnedGraph) return;

      searchParams.delete("spirometryTestId");
      setSearchParams(searchParams);
    };
  }, []);

  const getMeasurementTimeBySpirometryTestId = (
    spirometryTestIdValue: string
  ) => {
    let date = "";
    allMeasurements.forEach((measurement: any) => {
      measurement.forEach((entry: SelectMeasurementPayloadType) => {
        if (
          entry.spirometryTestIds &&
          entry.spirometryTestIds.find(
            (id) => id === parseInt(spirometryTestIdValue, 10)
          )
        ) {
          date = entry.time;
        }
      });
    });
    return date;
  };

  useEffect(() => {
    const setSelectChartValue = (value: any) =>
      onSelectChartValue && onSelectChartValue(value);
    if (spirometryTestIdValue) {
      setSelectChartValue({
        spirometryTestIds: [parseInt(spirometryTestIdValue, 10)],
        time: getMeasurementTimeBySpirometryTestId(spirometryTestIdValue),
      } as SelectMeasurementPayloadType);
    } else {
      setSelectChartValue(null);
    }
  }, [spirometryTestIdValue]);

  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    setSpirometryTestIdValue(null);
    setSelectedPoint(null);
    shouldAutoSelectLastTest.current = false;
    closeTreatmentDetails?.();

    if (!isRefetching) {
      const setSelectChartValue = (value: any) =>
        onSelectChartValue && onSelectChartValue(value);

      setSelectChartValue(null);
      refetch();
      treatmentsRefetch();
    }
  }, [
    filter,
    portalPreferences.customFromDate,
    portalPreferences.customToDate,
    preferences,
    portalPreferences.scaleYAxis,
  ]);

  useEffect(() => {
    let minDate: dayjs.Dayjs = dayjs("0001-01-01T00:00:00Z");
    if (
      data &&
      data.length > 0 &&
      data[0] &&
      (data[0] as any).mainMeasurementValues.minDate
    ) {
      const { mainMeasurementValues } = data[0] as any;
      minDate = mainMeasurementValues.minDate;
    }

    let startDateRange =
      dateRangeFilter.id === DATE_FILTER_ALL
        ? minDate
        : dateRangeFilter.startDate;

    if (
      measurementType === "nebulizer" &&
      dateRangeFilter.id === DATE_FILTER_ALL
    ) {
      const result = getNebulizerMinAndMaxDates();

      startDateRange = dayjs(result.min);
    }

    setFormattedDateRange(
      `${getShortFormattedDate(
        startDateRange.format()
      )} - ${getShortFormattedDate(dateRangeFilter.endDate.format())}`
    );

    // Only once it is a real date. `minDate` starts at year 1 as its own sentinel, and seeding
    // the picker with that would trade one unreadable date for another.
    const resolvedStart = dayjs(startDateRange);
    setDataStartDate(
      resolvedStart.isValid() && resolvedStart.year() > 1 ? resolvedStart : null
    );
  }, [data]);

  const domainByGlobalPreferences = useMemo(() => {
    if (portalPreferences.scaleYAxis) {
      return [
        (dataMin: number) => Math.floor(dataMin),
        (dataMax: number) => Math.ceil(dataMax),
      ] as AxisDomain;
    }

    if (mainValues.yAxisMin != null && mainValues.yAxisMax != null) {
      return [mainValues.yAxisMin, mainValues.yAxisMax] as AxisDomain;
    }
    if (chartConfigs.length > 0 && chartConfigs[0].min && chartConfigs[0].max) {
      const max = chartConfigs[0].max ?? 0;
      const min = chartConfigs[0].min ?? 0;
      const minRounded = Math.floor(min);
      return [
        minRounded,
        (dataMax: number) =>
          dataMax > max ? Math.ceil(dataMax) : Math.ceil(max),
      ] as AxisDomain;
    }

    return [0, (dataMax: number) => Math.ceil(dataMax)] as AxisDomain;
  }, [portalPreferences.scaleYAxis, chartConfigs]);

  const measurementChartWidth = useMemo(() => {
    if (isPinnedGraph) {
      return "100%";
    }

    if (measurementType === "nebulizer") {
      return 800;
    }

    if (widthView > 1530) {
      return isSpirometry ? 834 : 968;
    }
    if (widthView < 1250) {
      return isSpirometry ? 550 : 834;
    }
    return isSpirometry ? 664 : 928;
  }, [widthView]);

  const handleOnChangeModalOptions = (options: ModalOptions) => {
    changeUserPreferences(options);
  };

  const handledDisplayChange = (filter: string) => {
    setFilter(filter);

    const userUnitPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);
    if (!userUnitPreferences) return;

    const parsedUnitPreferences = safeJsonParse<IUserPreferences | null>(
      userUnitPreferences,
      null
    );

    if (!parsedUnitPreferences) return;

    let stringifiedDataPreferences = "";

    const dataPreferences = JSON.parse(
      parsedUnitPreferences.dataDisplayPreference
    ) as IDataDisplayPreferences;

    if (dataPreferences) {
      if (isSpirometry) {
        dataPreferences[measurementType as keyof IDataDisplayPreferences] =
          filter;
      } else {
        dataPreferences["default" as keyof IDataDisplayPreferences] = filter;
      }
      stringifiedDataPreferences = JSON.stringify(dataPreferences);
      const newUnitPreferences: IUserPreferences = {
        ...parsedUnitPreferences,
        dataDisplayPreference: stringifiedDataPreferences,
      };

      changeUnitPreferences(newUnitPreferences);
    }

    userService
      .updateUserDataDisplayPreference(stringifiedDataPreferences)
      .then(() => {
        if (filter === "BestOfDay") {
          mixpanelActions.track(`User action: DataDisplayBestOfTheDay`);
        } else {
          mixpanelActions.track(`User action: DataDisplayAlldataPoints`);
        }
      });
  };

  const handleTrendLineDisplayChange = (displayValue: boolean) => {
    const userUnitPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);
    if (!userUnitPreferences) return;

    const parsedUnitPreferences = safeJsonParse<IUserPreferences | null>(
      userUnitPreferences,
      null
    );

    if (!parsedUnitPreferences) return;

    let trendLinePreferences;

    try {
      trendLinePreferences = JSON.parse(
        parsedUnitPreferences.trendLineDisplayPreferences
      );
      if (trendLinePreferences) {
        trendLinePreferences[measurementType] = displayValue;
      }
    } catch {
      trendLinePreferences = {
        [measurementType]: displayValue,
      };
    }

    const newUnitPreferences: IUserPreferences = {
      ...parsedUnitPreferences,
      trendLineDisplayPreferences: JSON.stringify(trendLinePreferences),
    };
    changeUnitPreferences(newUnitPreferences);

    userService
      .updateTrendLinePreferences(measurementType, displayValue)
      .then(() => {
        if (displayValue) {
          mixpanelActions.track(`User action: Trend line On`);
        } else {
          mixpanelActions.track(`User action: Trend line Off`);
        }
      });

    setShowTrendLine(displayValue);
  };

  const handleOnChangeScaleYAxis = (scaleYAxis: boolean) => {
    const newPortalPreferences = { ...portalPreferences, scaleYAxis };
    changePortalPreferences(newPortalPreferences);
    userService.updateScaleYAxis(scaleYAxis);
  };

  const handleOnChangeDateRangeFilter = (
    dateRangeFilter: string,
    customFromDate?: dayjs.Dayjs,
    customToDate?: dayjs.Dayjs
  ) => {
    const newPortalPreferences = {
      ...portalPreferences,
      dateRangeFilter,
      customFromDate,
      customToDate,
    };
    changePortalPreferences(newPortalPreferences);

    if (dateRangeFilter !== "custom") {
      userService.updateDateRangeFilter(dateRangeFilter);
    }
  };

  const handleOnBaselineChange = (notification: Notification) => {
    showToast(notification.message, notification.type);

    if (notification.type === "success") {
      refetch();
    }
  };

  const customChartConfig = chartConfigs.map((currentChartConfig) => ({
    ...currentChartConfig,
    dot: isSpirometry,
  }));

  const highestOfDayLabel = filter
    ? ` ${t("patientPage_measurementModal_subtitle_highestOfDay")} `
    : "";

  // EN-3704: predicted FVC/FEV1 share the quality filter of the raw value they
  // are derived from, so the subtitle follows the same mapping.
  const qualityFilterType = spirometryQualityFilterTypes.get(measurementType);

  const acceptabilityHideUsableLabel =
    (hideFev1Usable && qualityFilterType === "fev1") ||
    (hideFvcUsable && qualityFilterType === "fvc")
      ? ` ${t("patientPage_measurementModal_subtitle_usableHidden")} `
      : "";

  const acceptabilityHideRejectedLabel =
    (hideFev1Rejected && qualityFilterType === "fev1") ||
    (hideFvcRejected && qualityFilterType === "fvc")
      ? ` ${t("patientPage_measurementModal_subtitle_rejectedHidden")} `
      : "";

  const isMontlhyAverage = () => {
    if (measurementType !== "nebulizer") return "";

    if (
      treatments.length > 0 &&
      dateRangeFilter.endDate.diff(dateRangeFilter.startDate, "month") >= 6 &&
      dateRangeFilter.id !== DATE_FILTER_ALL
    ) {
      return ` ${t("patientPage_measurementModal_subtitle_monthlyAverage")} `;
    }
    if (dateRangeFilter.id === DATE_FILTER_ALL) {
      const result = getNebulizerMinAndMaxDates();
      const minDate = result.min ? dayjs(result.min) : undefined;
      const maxDate = result.max ? dayjs(result.max) : undefined;
      if (minDate && maxDate && maxDate.diff(minDate, "month") >= 1) {
        return ` ${t("patientPage_measurementModal_subtitle_monthlyAverage")} `;
      }
    }

    return "";
  };

  const subTitle = `${formattedDateRange}${highestOfDayLabel}${acceptabilityHideUsableLabel}${acceptabilityHideRejectedLabel}${isMontlhyAverage()}`;

  const isEmpty =
    measurementType === "nebulizer"
      ? treatments.length === 0
      : allMeasurements.size === 0;

  const hasActiveSpirometryFilters =
    (qualityFilterType === "fev1" && (hideFev1Rejected || hideFev1Usable)) ||
    (qualityFilterType === "fvc" && (hideFvcRejected || hideFvcUsable)) ||
    (measurementType === "pef" &&
      (hideFvcRejected || hideFvcUsable || hideFev1Rejected || hideFev1Usable));

  const emptyMessage = t("patientPage_measurementModal_empty_noData");
  const emptySecondaryMessage = hasActiveSpirometryFilters
    ? t("patientPage_measurementModal_empty_tryRemoving")
    : undefined;

  const getBaselinesForOptionsBar = () => {
    const baselines: BaselineListOptionsBar = {
      systolicBaseline: null,
      diastolicBaseline: null,
      measurementBaseline: null,
    };

    if (measurementType === "bp_systolic") {
      const systolicBaseline =
        allPatientBaselines.get("bp_systolic")?.activeBaseline;

      const diastolicBaseline =
        allPatientBaselines.get("bp_diastolic")?.activeBaseline;

      if (systolicBaseline) baselines.systolicBaseline = systolicBaseline;
      if (diastolicBaseline) baselines.diastolicBaseline = diastolicBaseline;
    } else {
      const measurementBaseline =
        allPatientBaselines.get(measurementType)?.activeBaseline;

      if (measurementBaseline)
        baselines.measurementBaseline = measurementBaseline;
    }
    return baselines;
  };

  const getBaselines = () => {
    if (measurementType === "bp_systolic") {
      const systolicBaselines =
        allPatientBaselines.get("bp_systolic")?.baselines;
      const diastolicBaselines =
        allPatientBaselines.get("bp_diastolic")?.baselines;

      return systolicBaselines?.concat(diastolicBaselines ?? []) ?? [];
    }
    return (
      allPatientBaselines
        .get(measurementType)
        ?.baselines?.filter((pb) =>
          ishltBaselineActive ? pb : !pb.isISHLTBaseline
        ) ?? []
    );
  };

  const handleModalChart = () => {
    if (measurementType === "nebulizer" && patientId) {
      const result = getNebulizerMinAndMaxDates();

      const minDate = result.min ? dayjs(result.min) : undefined;
      const maxDate = result.max ? dayjs(result.max) : undefined;

      return (
        <NebulizerModalChart
          data={treatments}
          height="78%"
          width={measurementChartWidth}
          isPinnedGraph={isPinnedGraph}
          dateInterval={dateRangeFilter}
          minDate={minDate}
          maxDate={maxDate}
          onClick={onTreatmentClick}
          isEmpty={isEmpty}
          emptyMessage={emptyMessage}
        />
      );
    }

    return (
      <ModalChart
        onRowClick={
          onSelectChartValue &&
          ((selectedMeasurementData) => {
            onSelectChartValue(selectedMeasurementData);
          })
        }
        height="78%"
        showTrendLine={showTrendLine}
        data={allMeasurements || new Map<string, any[]>()}
        mainValues={mainValues}
        diastolicMainValues={diastolicMainValues}
        width={measurementChartWidth}
        chartConfig={customChartConfig}
        yConfig={{ domain: domainByGlobalPreferences }}
        dateInterval={dateRangeFilter}
        isSpirometry={isSpirometry}
        isPinnedGraph={isPinnedGraph}
        measurementType={measurementType}
        patientBaselines={getBaselines()}
        minValue={minValue}
        maxValue={maxValue}
        isEmpty={isEmpty}
        emptyMessage={emptyMessage}
        emptySecondaryMessage={emptySecondaryMessage}
      />
    );
  };

  const dialysisMeasurementTypes: MeasurementTypes[] = [
    "dialysis_bp_systolic",
    "dialysis_bp_diastolic",
    "dialysis_weight",
    "uf_volume",
  ];

  const handleModalTable = () => {
    if (measurementType === "nebulizer") {
      return (
        <ModalNebulizerTable
          data={treatments}
          isPinnedGraph={isPinnedGraph}
          onRowClick={onTreatmentClick}
        />
      );
    }

    if (dialysisMeasurementTypes.includes(measurementType)) {
      return (
        <ModalDialysisTable
          patientId={patientId}
          fromDate={dateRangeFilter.startDate.format()}
          toDate={dateRangeFilter.endDate.format()}
          isPinnedGraph={isPinnedGraph}
        />
      );
    }

    return (
      <ModalMeasurementsTable
        data={allMeasurements}
        measurementType={measurementType}
        patientId={patientId}
        isPinnedGraph={isPinnedGraph}
        unit={unit}
      />
    );
  };

  return isFetching ||
    isRefetching ||
    isTreatmentsFetching ||
    isTreatmentsRefetching ? (
    <ModalSkeleton
      isPinnedGraph={isPinnedGraph}
      width={measurementChartWidth}
    />
  ) : (
    <MeasurementContainer
      style={
        measurementType === "arrhythmia_status"
          ? { minWidth: measurementChartWidth }
          : undefined
      }
    >
      <HeaderContainer
        style={
          showMeasurementsTable
            ? { marginBottom: "0px" }
            : { marginBottom: "20px" }
        }
      >
        <Title>{cardTitle}</Title>
        <Subtitle>{subTitle}</Subtitle>

        <SubtitleConatiner>
          <CustomOptionsBar
            onChange={(selectedDateFilter) => {
              const { id } = selectedDateFilter;

              if (id === DATE_FILTER_CUSTOM || id !== dateRangeFilter.id) {
                handleOnChangeDateRangeFilter(
                  id,
                  selectedDateFilter.startDate,
                  selectedDateFilter.endDate
                );
              }
            }}
            onTableViewChange={(enableTableView) => {
              setShowMeasurementsTable(enableTableView);
              onSelectedTableView(enableTableView);
            }}
            showTrendLine={showTrendLine}
            onChangeShowTrendLine={handleTrendLineDisplayChange}
            selectedFilterDate={dateRangeFilter}
            dataStartDate={dataStartDate}
            cardTitle={cardTitle}
            initialScaleYAxis={portalPreferences.scaleYAxis ?? false}
            initialOptions={preferences}
            initialDisplay={getGraphPreferences()}
            onChangeOption={handleOnChangeModalOptions}
            onChangeScaleYAxis={handleOnChangeScaleYAxis}
            onDisplayChange={handledDisplayChange}
            onBaselineChange={handleOnBaselineChange}
            measurementType={measurementType}
            patientId={patientId}
            ishltBaselineActive={ishltBaselineActive}
            wardISHLTBaselineActive={wardISHLTBaselineActive}
            baselineValues={getBaselinesForOptionsBar()}
            baselines={allPatientBaselines.get(measurementType)}
            ishltBaseline={
              measurementType === "fev1"
                ? allPatientBaselines.get(measurementType)?.activeISHLTBaseline
                : undefined
            }
            unit={unit}
            isTableView={showMeasurementsTable}
            isEmpty={isEmpty}
          />
        </SubtitleConatiner>
      </HeaderContainer>

      {!showMeasurementsTable ? handleModalChart() : handleModalTable()}
    </MeasurementContainer>
  );
}
